import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, AlertTriangle, CheckCircle, XCircle, Plus, Filter, TrendingUp, Map as MapIconLucid } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { roadStatuses, northeastStates } from '../mockDataVegrExpanded';
import { getAllReports, getReportStats } from '../services/apiService';
import VegrMapMobile from '../components/VegrMapMobile';

const VegrHomePageNew = () => {
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedState, setSelectedState] = useState(null);
  const [allReports, setAllReports] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    clear: 0,
    damaged: 0,
    blocked: 0,
    byState: {}
  });
  const [loading, setLoading] = useState(true);

  // Fetch reports from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [reportsData, statsData] = await Promise.all([
          getAllReports(),
          getReportStats()
        ]);
        
        setAllReports(reportsData);
        setStats(statsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        // If backend fails, keep empty data (no mock fallback)
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'clear':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'damaged':
        return <AlertTriangle className="w-4 h-4 text-amber-600" />;
      case 'blocked':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <MapPin className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    const statusObj = roadStatuses.find(s => s.value === status);
    return statusObj ? statusObj.color : '#6B7280';
  };

  const filteredReports = allReports.filter(report => {
    const statusMatch = selectedStatus === 'all' || report.status === selectedStatus;
    const stateMatch = !selectedState || report.state === selectedState;
    return statusMatch && stateMatch;
  });

  const recentReports = filteredReports.slice(0, 10);

  return (
    <div className="min-h-screen bg-[#E8DED0]">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2C4A5E] to-[#1e3544] text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold mb-2">Vegr</h1>
              <p className="text-sm md:text-base text-white/80">Northeast India Road Monitoring</p>
            </div>
            <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
              <MapPin className="w-8 h-8 md:w-10 md:h-10" />
            </div>
          </div>
          
          {/* Quick Stats Bar */}
          <div className="grid grid-cols-4 gap-2 md:gap-4 mb-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 md:p-3 text-center">
              <div className="text-2xl md:text-3xl font-bold">{loading ? '...' : stats.total}</div>
              <div className="text-xs text-white/70">Total</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 md:p-3 text-center">
              <div className="text-2xl md:text-3xl font-bold text-green-400">{loading ? '...' : stats.clear}</div>
              <div className="text-xs text-white/70">Clear</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 md:p-3 text-center">
              <div className="text-2xl md:text-3xl font-bold text-amber-400">{loading ? '...' : stats.damaged}</div>
              <div className="text-xs text-white/70">Damaged</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 md:p-3 text-center">
              <div className="text-2xl md:text-3xl font-bold text-red-400">{loading ? '...' : stats.blocked}</div>
              <div className="text-xs text-white/70">Blocked</div>
            </div>
          </div>
        </div>
      </section>

      {loading && (
        <div className="py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C4A5E] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading real-time data...</p>
        </div>
      )}

      {!loading && allReports.length === 0 && (
        <div className="py-12 text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Reports Yet</h3>
          <p className="text-gray-600 mb-4">Be the first to report road conditions!</p>
          <Link to="/vegr/report">
            <Button className="bg-[#4A7C59] hover:bg-[#3d6949]">
              <Plus className="w-4 h-4 mr-2" />
              Submit First Report
            </Button>
          </Link>
        </div>
      )}

      <section className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <Filter className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <button
              onClick={() => setSelectedStatus('all')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedStatus === 'all'
                  ? 'bg-[#2C4A5E] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({stats.total})
            </button>
            <button
              onClick={() => setSelectedStatus('clear')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedStatus === 'clear'
                  ? 'bg-[#4A7C59] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Clear ({stats.clear})
            </button>
            <button
              onClick={() => setSelectedStatus('damaged')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedStatus === 'damaged'
                  ? 'bg-[#C68B3C] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Damaged ({stats.damaged})
            </button>
            <button
              onClick={() => setSelectedStatus('blocked')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedStatus === 'blocked'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Blocked ({stats.blocked})
            </button>
          </div>
        </div>
      </section>

      <section className="py-3 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <MapIconLucid className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <button
              onClick={() => setSelectedState(null)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                !selectedState
                  ? 'bg-[#2C4A5E] text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
              }`}
            >
              All States
            </button>
            {northeastStates.map((state) => (
              <button
                key={state.name}
                onClick={() => setSelectedState(state.name)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  selectedState === state.name
                    ? 'bg-[#2C4A5E] text-white'
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                {state.name} ({stats.byState[state.name]})
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-4 md:py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Live Map</h2>
              <p className="text-sm text-gray-600">Showing {filteredReports.length} reports</p>
            </div>
            <Badge className="bg-green-500 text-white px-3 py-1">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span className="text-xs">Live</span>
              </div>
            </Badge>
          </div>
          
          <VegrMapMobile 
            allReports={allReports}
            height="500px" 
            selectedStatus={selectedStatus === 'all' ? null : selectedStatus}
            selectedState={selectedState}
          />
        </div>
      </section>

      <section className="py-6 bg-[#E8DED0]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Recent Reports</h2>
            <TrendingUp className="w-5 h-5 text-[#2C4A5E]" />
          </div>
          
          <div className="space-y-3">
            {recentReports.map((report) => (
              <Card key={report.id} className="hover:shadow-lg transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div 
                      className="p-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: `${getStatusColor(report.status)}20` }}
                    >
                      {getStatusIcon(report.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge 
                          className="text-white text-xs"
                          style={{ backgroundColor: getStatusColor(report.status) }}
                        >
                          {report.status.toUpperCase()}
                        </Badge>
                        <Badge variant="outline" className="text-xs">{report.state}</Badge>
                        {report.verified && (
                          <Badge className="bg-green-600 text-white text-xs">Verified</Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-1">{report.location}</h3>
                      <p className="text-xs text-gray-600 mb-1">{report.roadName}</p>
                      <p className="text-xs text-gray-700">{report.description}</p>
                      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <span>{report.reportedBy}</span>
                        <span>{new Date(report.timestamp).toLocaleTimeString()}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">State-wise Reports</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {northeastStates.map((state) => (
              <Card 
                key={state.name}
                className="hover:shadow-lg transition-all cursor-pointer"
                onClick={() => setSelectedState(state.name)}
              >
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-[#2C4A5E] mb-1">
                    {stats.byState[state.name]}
                  </div>
                  <div className="text-sm text-gray-700 font-medium">{state.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Link to="/vegr/report">
        <button className="fixed bottom-6 right-6 w-16 h-16 bg-[#4A7C59] hover:bg-[#3d6949] text-white rounded-full shadow-2xl flex items-center justify-center z-50 transition-transform hover:scale-110">
          <Plus className="w-8 h-8" />
        </button>
      </Link>
    </div>
  );
};

export default VegrHomePageNew;